/**
 * Izba Admin — Metric / KPI definitions.
 *
 * Shared platform catalog (no org scope). Uses shared Table + admin controls.
 * New metric is disabled for now.
 */
import React, { useEffect, useMemo, useState } from "react";

import {
  Check,
  ChevronsUpDown,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  X,
} from "lucide-react";

import { NormalizationField } from "./NormalizationField";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import SearchInput from "../../Components/Search/SearchInput";
import Table from "../../Components/Table";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  bulkUpdateDataHubDefinitions,
  deleteDataHubDefinition,
  fetchDataHubCategories,
  fetchDataHubDefinitions,
  updateDataHubDefinition,
  type DataHubKpiCategory,
  type DataHubMetricDefinition,
} from "../../Store/Reducers/DataHubSlice";

const NORMALIZATION_TYPES = [
  "percentile_ranking",
  "inverse_percentile",
  "linear_scaling",
  "custom_scaling",
  "direct_mapping",
  "inverse_mapping",
];
const NORMALIZATION_DIRECTIONS = ["higher_better", "lower_better", "neutral"];
const CADENCE_DEFAULTS = [
  "Daily",
  "Weekly",
  "Monthly",
  "Quarterly",
  "Annually",
  "Ad-hoc",
];
const TIME_WINDOW_DEFAULTS = [
  "Past Week",
  "Past Month",
  "Past Quarter",
  "Past 6 Months",
  "Past Year",
  "Lifetime",
];

type ColKey =
  | "metric_id"
  | "metric_name"
  | "category"
  | "subcategory"
  | "category_id"
  | "unit_of_measure"
  | "format"
  | "scale"
  | "normalization_type"
  | "normalization_direction"
  | "kpi_status"
  | "export_cadence"
  | "export_time_window"
  | "business_impact"
  | "formula"
  | "source_system"
  | "source_table"
  | "description";

type ColKind = "text" | "combo" | "enum";

const TABLE_COLUMNS: {
  key: ColKey;
  title: string;
  width: string;
  kind: ColKind;
}[] = [
  { key: "metric_id", title: "metric_id", width: "180px", kind: "text" },
  { key: "metric_name", title: "Name", width: "320px", kind: "text" },
  { key: "category", title: "Category", width: "200px", kind: "combo" },
  { key: "subcategory", title: "Subcategory", width: "180px", kind: "combo" },
  { key: "category_id", title: "category_id", width: "140px", kind: "text" },
  { key: "unit_of_measure", title: "UOM", width: "100px", kind: "combo" },
  { key: "format", title: "Format", width: "130px", kind: "combo" },
  { key: "scale", title: "Scale", width: "120px", kind: "combo" },
  {
    key: "normalization_type",
    title: "Norm. type",
    width: "180px",
    kind: "enum",
  },
  {
    key: "normalization_direction",
    title: "Direction",
    width: "150px",
    kind: "enum",
  },
  { key: "kpi_status", title: "KPI status", width: "140px", kind: "combo" },
  { key: "export_cadence", title: "Cadence", width: "130px", kind: "combo" },
  {
    key: "export_time_window",
    title: "Time window",
    width: "150px",
    kind: "combo",
  },
  {
    key: "business_impact",
    title: "Business impact",
    width: "220px",
    kind: "text",
  },
  { key: "formula", title: "Formula", width: "200px", kind: "text" },
  {
    key: "source_system",
    title: "Source system",
    width: "160px",
    kind: "text",
  },
  { key: "source_table", title: "Source table", width: "160px", kind: "text" },
  { key: "description", title: "Description", width: "260px", kind: "text" },
];

type Facets = {
  category: string[];
  subcategory: string[];
  unit_of_measure: string[];
  format: string[];
  scale: string[];
  kpi_status: string[];
  export_cadence: string[];
  export_time_window: string[];
};

const cellValue = (
  row: DataHubMetricDefinition,
  key: ColKey
): string | null => {
  let raw: unknown = row[key as keyof DataHubMetricDefinition];
  if (key === "category") {
    raw = row.category || row.full_category_name || row.category_key;
  } else if (key === "category_id") {
    raw = row.category_id || row.category_key;
  }
  if (raw == null || raw === "") return null;
  return String(raw);
};

function DefinitionsPanel() {
  const dispatch = useAppDispatch();
  const {
    data: items,
    loading,
    error,
    saving,
  } = useAppSelector(s => s.dataHub.definitions);
  const { data: categories } = useAppSelector(s => s.dataHub.categories);

  const [search, setSearch] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [dirty, setDirty] = useState<
    Map<string, Partial<DataHubMetricDefinition>>
  >(new Map());
  const [editing, setEditing] = useState<DataHubMetricDefinition | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [skipConfirm, setSkipConfirm] = useState<{
    row: DataHubMetricDefinition;
    next: boolean;
  } | null>(null);

  useEffect(() => {
    void dispatch(fetchDataHubDefinitions());
    void dispatch(fetchDataHubCategories());
  }, [dispatch]);

  const facets: Facets = useMemo(() => {
    const uniq = (
      getter: (r: DataHubMetricDefinition) => string | null | undefined
    ) =>
      Array.from(
        new Set(
          items
            .map(getter)
            .filter((v): v is string => !!v && String(v).trim().length > 0)
            .map(String)
        )
      ).sort((a, b) => a.localeCompare(b));
    return {
      category: uniq(r => r.category || r.full_category_name),
      subcategory: uniq(r => r.subcategory),
      unit_of_measure: uniq(r => r.unit_of_measure),
      format: uniq(r => r.format),
      scale: uniq(r => r.scale),
      kpi_status: uniq(r => r.kpi_status),
      export_cadence: uniq(r => r.export_cadence),
      export_time_window: uniq(r => r.export_time_window),
    };
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(r => {
      const hay = [
        r.metric_id,
        r.metric_name,
        r.category,
        r.full_category_name,
        r.category_id,
        r.category_key,
        r.subcategory,
        r.unit_of_measure,
        r.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  const tableData = useMemo(() => {
    if (!bulkMode || dirty.size === 0) return filtered;
    return filtered.map(r => {
      const patch = dirty.get(r.metric_id);
      return patch ? { ...r, ...patch } : r;
    });
  }, [filtered, dirty, bulkMode]);

  const setCell = (
    metricId: string,
    key: ColKey,
    value: string | number | null
  ) => {
    setDirty(prev => {
      const next = new Map(prev);
      const base = items.find(r => r.metric_id === metricId);
      const current = { ...(next.get(metricId) ?? {}) };
      (current as Record<string, unknown>)[key] = value;
      if (base) {
        let hasDiff = false;
        for (const k of Object.keys(
          current
        ) as (keyof DataHubMetricDefinition)[]) {
          const v = current[k];
          const orig = base[k];
          const same =
            v == null && (orig == null || orig === "")
              ? true
              : String(v ?? "") === String(orig ?? "");
          if (same) delete (current as Record<string, unknown>)[k];
          else hasDiff = true;
        }
        if (!hasDiff || Object.keys(current).length === 0) {
          next.delete(metricId);
          return next;
        }
      }
      next.set(metricId, current);
      return next;
    });
  };

  const dirtyCount = dirty.size;

  const submitBulk = async () => {
    const payload: Array<{ metricId: string; data: Record<string, any> }> = [];
    for (const r of items) {
      const patch = dirty.get(r.metric_id);
      if (!patch) continue;
      const data: Record<string, any> = { ...patch };
      delete data.metric_id;
      delete data.id;
      payload.push({ metricId: r.metric_id, data });
    }
    if (!payload.length) return;
    const result = await dispatch(bulkUpdateDataHubDefinitions(payload));
    if (bulkUpdateDataHubDefinitions.fulfilled.match(result)) {
      setDirty(new Map());
    }
  };

  const requestSkipToggle = (row: DataHubMetricDefinition, next: boolean) => {
    setSkipConfirm({ row, next });
  };

  const confirmSkipToggle = async () => {
    if (!skipConfirm) return;
    const { row, next } = skipConfirm;
    const result = await dispatch(
      updateDataHubDefinition({
        metricId: row.metric_id,
        data: { skip: next },
        silent: true,
      })
    );
    if (updateDataHubDefinition.fulfilled.match(result)) {
      setSkipConfirm(null);
    }
  };

  const saveEdit = async (row: DataHubMetricDefinition) => {
    const data: Record<string, any> = {
      metric_name: row.metric_name,
      category: row.category || row.full_category_name || null,
      category_id: row.category_id || row.category_key || null,
      subcategory: row.subcategory || null,
      unit_of_measure: row.unit_of_measure || null,
      format: row.format || null,
      scale: row.scale || null,
      normalization_type: row.normalization_type || null,
      normalization_direction: row.normalization_direction || null,
      kpi_status: row.kpi_status || null,
      export_cadence: row.export_cadence || null,
      export_time_window: row.export_time_window || null,
      business_impact: row.business_impact || null,
      formula: row.formula || null,
      source_system: row.source_system || null,
      source_table: row.source_table || null,
      description: row.description || null,
      enabled: row.enabled !== false,
      skip: !!row.skip,
    };
    const result = await dispatch(
      updateDataHubDefinition({ metricId: row.metric_id, data })
    );
    if (updateDataHubDefinition.fulfilled.match(result)) {
      setEditing(null);
    }
  };

  const remove = async (metricId: string) => {
    const result = await dispatch(deleteDataHubDefinition(metricId));
    if (deleteDataHubDefinition.fulfilled.match(result)) {
      setConfirmDeleteId(null);
      setEditing(null);
    }
  };

  const renderDefinitionCell = (
    key: ColKey,
    kind: ColKind,
    record: DataHubMetricDefinition
  ): React.ReactNode => {
    const val = cellValue(record, key);
    if (bulkMode && key !== "metric_id") {
      return (
        <CellEditor
          colKey={key}
          kind={kind}
          value={val}
          facets={facets}
          onChange={v => setCell(record.metric_id, key, v)}
        />
      );
    }
    if (key === "metric_id" || key === "category_id") {
      return (
        <span className="font-mono text-xs text-gray-700">{val ?? "—"}</span>
      );
    }
    if (key === "metric_name") {
      return (
        <div>
          <div className="text-sm font-medium text-gray-900">{val ?? "—"}</div>
          {dirty.has(record.metric_id) && (
            <span className="mt-0.5 inline-block text-[10px] font-medium uppercase tracking-wide text-amber-700">
              Unsaved
            </span>
          )}
        </div>
      );
    }
    if (key === "category" && val) {
      return (
        <span className="inline-flex rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700">
          {val}
        </span>
      );
    }
    return <span className="text-sm text-gray-600">{val ?? "—"}</span>;
  };

  const columns = useMemo(
    () => [
      {
        key: "skip",
        title: "Skip",
        width: "90px",
        textalign: "center" as const,
        sortable: false,
        render: (_: any, record: DataHubMetricDefinition) => (
          <div className="flex justify-center">
            <ToggleSwitch
              id={`skip-${record.metric_id}`}
              checked={!!record.skip}
              disabled={saving || bulkMode}
              onChange={checked => requestSkipToggle(record, checked)}
            />
          </div>
        ),
      },
      ...TABLE_COLUMNS.map(col => ({
        key: col.key,
        title: col.title,
        width: col.width,
        sortable: true,
        sortValue: (record: DataHubMetricDefinition) =>
          cellValue(record, col.key) || "",
        render: (_: any, record: DataHubMetricDefinition) =>
          renderDefinitionCell(col.key, col.kind, record),
      })),
      ...(bulkMode
        ? []
        : [
            {
              key: "actions",
              title: "",
              width: "72px",
              textalign: "center" as const,
              sortable: false,
              render: (_: any, record: DataHubMetricDefinition) => (
                <Button
                  type="button"
                  variant="clear"
                  title="Edit"
                  className="!h-8 !w-8 !min-w-0 !justify-center !p-0 text-gray-500 hover:text-violet-700"
                  onClick={() => setEditing(record)}
                >
                  <Pencil size={16} />
                </Button>
              ),
            },
          ]),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bulkMode, saving, facets, dirty]
  );

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <div className="min-w-[240px] max-w-sm flex-1">
            <SearchInput
              placeholder="Search metric_id, name, or category…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span className="text-sm text-gray-500">
            {filtered.length} metric{filtered.length === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5">
            <ToggleSwitch
              id="definitions-bulk-mode"
              checked={bulkMode}
              onChange={checked => {
                setBulkMode(checked);
                if (!checked) setDirty(new Map());
              }}
            />
            <label
              htmlFor="definitions-bulk-mode"
              className="cursor-pointer text-sm text-gray-700"
            >
              Bulk edit
            </label>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline-primary"
            disabled={loading || saving}
            onClick={() => void dispatch(fetchDataHubDefinitions())}
            icon={
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
            }
          >
            Refresh
          </Button>
          {bulkMode ? (
            <>
              <Button
                type="button"
                variant="outline-primary"
                disabled={dirtyCount === 0 || saving}
                onClick={() => setDirty(new Map())}
                icon={<RotateCcw className="h-4 w-4" />}
              >
                Discard
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={dirtyCount === 0 || saving}
                loading={saving}
                onClick={() => void submitBulk()}
                icon={<Save className="h-4 w-4" />}
              >
                Save all
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="primary"
              disabled
              title="Creating new metrics is disabled for now"
              icon={<Plus className="h-4 w-4" />}
            >
              New metric
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm">
        <Table
          className="definitions-metrics-table [&_th]:normal-case [&_th]:tracking-normal [&_th]:whitespace-nowrap [&_td]:align-middle"
          columns={columns}
          data={tableData}
          loading={loading}
          sortable
          scrollable
          maxHeight="calc(100vh - 22rem)"
          stickyLastColumn={!bulkMode}
          rowKey={record => record.metric_id}
          emptyContent={
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">
                No metric definitions match
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Try a different search, or refresh the catalog.
              </p>
            </div>
          }
        />
      </div>

      {bulkMode && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <span className="text-sm text-gray-500">
            {dirtyCount === 0
              ? "No changes — edit any cell to enable Save all."
              : `${dirtyCount} row${dirtyCount === 1 ? "" : "s"} changed`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline-primary"
              disabled={dirtyCount === 0 || saving}
              onClick={() => setDirty(new Map())}
              icon={<RotateCcw className="h-4 w-4" />}
            >
              Discard
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={dirtyCount === 0 || saving}
              loading={saving}
              onClick={() => void submitBulk()}
              icon={<Save className="h-4 w-4" />}
            >
              Save all
            </Button>
          </div>
        </div>
      )}

      <CommonModal
        isOpen={!!editing && !bulkMode}
        toggle={() => setEditing(null)}
        title={editing ? `Edit ${editing.metric_id}` : "Edit metric"}
        subtitle="Fields marked * are required. category_id is picked from the MASTER KPI Categories catalog; the Category label auto-fills from your selection."
        size="5xl"
        usePortal
        contentClassName="overflow-hidden flex flex-col max-h-[85vh]"
      >
        {editing && (
          <MetricEditPanel
            row={editing}
            facets={facets}
            categories={categories}
            saving={saving}
            onCancel={() => setEditing(null)}
            onSave={row => void saveEdit(row)}
            onDelete={id => setConfirmDeleteId(id)}
          />
        )}
      </CommonModal>

      <CommonModal
        isOpen={!!skipConfirm}
        toggle={() => setSkipConfirm(null)}
        title={
          skipConfirm?.next ? "Skip this metric?" : "Include this metric again?"
        }
        size="md"
        usePortal
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline-primary"
              disabled={saving}
              onClick={() => setSkipConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={saving || !skipConfirm}
              loading={saving}
              onClick={() => void confirmSkipToggle()}
            >
              {skipConfirm?.next ? "Skip metric" : "Include metric"}
            </Button>
          </div>
        }
      >
        {skipConfirm ? (
          <div className="space-y-2 text-sm text-gray-600">
            <p className="m-0">
              {skipConfirm.next
                ? "Skipped metrics are excluded from Phase 2 pipeline steps (Benchmark, Normalize, Summarize, Category Scores)."
                : "This metric will be included again in Phase 2 pipeline steps."}
            </p>
            <p className="m-0">
              Metric:{" "}
              <span className="font-mono text-gray-900">
                {skipConfirm.row.metric_id}
              </span>
              {skipConfirm.row.metric_name
                ? ` — ${skipConfirm.row.metric_name}`
                : null}
            </p>
            <p className="m-0 text-xs text-gray-500">
              This change applies to the shared platform catalog for every
              organization.
            </p>
          </div>
        ) : null}
      </CommonModal>

      <CommonModal
        isOpen={!!confirmDeleteId}
        toggle={() => setConfirmDeleteId(null)}
        title="Delete this metric?"
        size="md"
        usePortal
        nested
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline-primary"
              disabled={saving}
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={saving || !confirmDeleteId}
              loading={saving}
              onClick={() => confirmDeleteId && void remove(confirmDeleteId)}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="m-0 text-sm text-gray-600">
          You&apos;re about to delete{" "}
          <span className="font-mono">{confirmDeleteId}</span> from the shared
          catalog. This cannot be undone.
        </p>
      </CommonModal>
    </div>
  );
}

function CellEditor({
  colKey,
  kind,
  value,
  facets,
  onChange,
}: {
  colKey: ColKey;
  kind: ColKind;
  value: string | null;
  facets: Facets;
  onChange: (v: string | number | null) => void;
}) {
  if (kind === "combo") {
    const facetOptions =
      colKey === "category"
        ? facets.category
        : colKey === "subcategory"
          ? facets.subcategory
          : colKey === "unit_of_measure"
            ? facets.unit_of_measure
            : colKey === "format"
              ? facets.format
              : colKey === "scale"
                ? facets.scale
                : colKey === "kpi_status"
                  ? facets.kpi_status
                  : colKey === "export_cadence"
                    ? [...CADENCE_DEFAULTS, ...facets.export_cadence]
                    : colKey === "export_time_window"
                      ? [...TIME_WINDOW_DEFAULTS, ...facets.export_time_window]
                      : [];
    const options = Array.from(new Set(facetOptions));
    return (
      <ValueCombobox
        value={value}
        options={options}
        onChange={v => onChange(v)}
        allowCustom
      />
    );
  }
  if (kind === "enum") {
    return (
      <ValueCombobox
        value={value}
        options={
          colKey === "normalization_type"
            ? NORMALIZATION_TYPES
            : NORMALIZATION_DIRECTIONS
        }
        onChange={v => onChange(v)}
        allowCustom={false}
      />
    );
  }
  return (
    <input
      type="text"
      value={value ?? ""}
      onChange={e => onChange(e.target.value || null)}
      className={`h-9 w-full rounded-md border border-gray-300 px-2 text-sm ${
        colKey === "metric_name" ? "min-w-[280px]" : ""
      }`}
    />
  );
}

function ValueCombobox({
  value,
  options,
  onChange,
  allowCustom,
  placeholder = "Select…",
}: {
  value: string | null;
  options: string[];
  onChange: (v: string | null) => void;
  allowCustom: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const trimmed = query.trim();
  const filtered = options.filter(o =>
    o.toLowerCase().includes(trimmed.toLowerCase())
  );
  const showAdd =
    allowCustom &&
    trimmed.length > 0 &&
    !options.some(o => o.toLowerCase() === trimmed.toLowerCase());

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-2 text-left text-sm"
      >
        <span
          className={`truncate ${!value ? "text-gray-400" : "text-gray-800"}`}
        >
          {value ?? placeholder}
        </span>
        <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 text-gray-400" />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label="Close"
            onClick={() => {
              setOpen(false);
              setQuery("");
            }}
          />
          <div className="absolute left-0 z-50 mt-1 w-[260px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={allowCustom ? "Search or type new…" : "Search…"}
              className="w-full border-b border-gray-100 px-3 py-2 text-sm outline-none"
            />
            <div className="max-h-48 overflow-auto py-1">
              {value && (
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  Clear value
                </button>
              )}
              {filtered.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-50"
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <Check
                    className={`mr-2 h-3.5 w-3.5 ${
                      value === opt ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  {opt}
                </button>
              ))}
              {showAdd && (
                <button
                  type="button"
                  className="flex w-full items-center px-3 py-2 text-left text-sm text-violet-700 hover:bg-violet-50"
                  onClick={() => {
                    onChange(trimmed);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <Plus className="mr-2 h-3.5 w-3.5" /> Use “{trimmed}”
                </button>
              )}
              {!filtered.length && !showAdd && (
                <p className="px-3 py-2 text-center text-sm text-gray-400">
                  No matches
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MetricEditPanel({
  row,
  facets,
  categories,
  saving,
  onCancel,
  onSave,
  onDelete,
}: {
  row: DataHubMetricDefinition;
  facets: Facets;
  categories: DataHubKpiCategory[];
  saving: boolean;
  onCancel: () => void;
  onSave: (row: DataHubMetricDefinition) => void;
  onDelete: (metricId: string) => void;
}) {
  const [draft, setDraft] = useState<DataHubMetricDefinition>(() => ({
    ...row,
    category_id: row.category_id || row.category_key || null,
    category: row.category || row.full_category_name || null,
  }));

  const baseline = useMemo(
    () => ({
      ...row,
      category_id: row.category_id || row.category_key || null,
      category: row.category || row.full_category_name || null,
    }),
    [row]
  );

  useEffect(() => {
    setDraft(baseline);
  }, [baseline]);

  const set = <K extends keyof DataHubMetricDefinition>(
    k: K,
    v: DataHubMetricDefinition[K]
  ) => setDraft(d => ({ ...d, [k]: v }));

  const isDirty = (
    Object.keys(draft) as (keyof DataHubMetricDefinition)[]
  ).some(k => String(draft[k] ?? "") !== String(baseline[k] ?? ""));

  const categoryId = draft.category_id || draft.category_key || "";
  const canSave =
    !!draft.metric_name?.trim() &&
    !!categoryId &&
    !!(draft.unit_of_measure ?? "").trim() &&
    !!(draft.format ?? "").trim() &&
    !!(draft.scale ?? "").trim() &&
    !!(draft.kpi_status ?? "").trim() &&
    !!(draft.normalization_type ?? "").trim() &&
    !!(draft.normalization_direction ?? "").trim() &&
    isDirty;

  const enabledCategories = categories.filter(c => c.enabled !== false);

  return (
    <div className="space-y-4 overflow-y-auto p-1">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Field label="metric_id">
          <input
            value={draft.metric_id}
            disabled
            className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-600"
          />
          <p className="mt-1 text-[10px] text-gray-500">
            Metric ID cannot be renamed here.
          </p>
        </Field>
        <Field label="Metric name" required className="md:col-span-2">
          <input
            value={draft.metric_name || ""}
            onChange={e => set("metric_name", e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="category_id" required>
          <select
            value={categoryId}
            onChange={e => {
              const id = e.target.value;
              const cat = enabledCategories.find(c => c.id === id);
              setDraft(d => ({
                ...d,
                category_id: id || null,
                category_key: id || null,
                category: cat ? cat.label : d.category,
                full_category_name: cat ? cat.label : d.full_category_name,
              }));
            }}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Select a category…</option>
            {enabledCategories.map(c => (
              <option key={c.id} value={c.id}>
                {c.id} — {c.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Category (auto-filled)">
          <input
            value={draft.category || draft.full_category_name || ""}
            readOnly
            disabled
            className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
          />
        </Field>
        <Field label="Subcategory">
          <ValueCombobox
            value={draft.subcategory || null}
            options={facets.subcategory}
            onChange={v => set("subcategory", v)}
            allowCustom
          />
        </Field>
        <Field label="Unit of measure" required>
          <ValueCombobox
            value={draft.unit_of_measure || null}
            options={facets.unit_of_measure}
            onChange={v => set("unit_of_measure", v)}
            allowCustom
          />
        </Field>
        <Field label="Format" required>
          <ValueCombobox
            value={draft.format || null}
            options={facets.format}
            onChange={v => set("format", v)}
            allowCustom
          />
        </Field>
        <Field label="Scale" required>
          <ValueCombobox
            value={draft.scale || null}
            options={facets.scale}
            onChange={v => set("scale", v)}
            allowCustom
          />
        </Field>
        <Field label="KPI status" required>
          <ValueCombobox
            value={draft.kpi_status || null}
            options={facets.kpi_status}
            onChange={v => set("kpi_status", v)}
            allowCustom
          />
        </Field>
        <Field label="Export cadence">
          <ValueCombobox
            value={draft.export_cadence || null}
            options={[
              ...CADENCE_DEFAULTS,
              ...facets.export_cadence.filter(
                o => !CADENCE_DEFAULTS.includes(o)
              ),
            ]}
            onChange={v => set("export_cadence", v)}
            allowCustom
          />
        </Field>
        <Field label="Export time window">
          <ValueCombobox
            value={draft.export_time_window || null}
            options={[
              ...TIME_WINDOW_DEFAULTS,
              ...facets.export_time_window.filter(
                o => !TIME_WINDOW_DEFAULTS.includes(o)
              ),
            ]}
            onChange={v => set("export_time_window", v)}
            allowCustom
          />
        </Field>
        <div className="md:col-span-2">
          <NormalizationField
            value={{
              normalization_type: (draft.normalization_type as any) ?? null,
              normalization_direction:
                (draft.normalization_direction as any) ?? null,
            }}
            onChange={v => {
              set("normalization_type", v.normalization_type);
              set("normalization_direction", v.normalization_direction);
            }}
          />
        </div>
        <Field label="Source system">
          <input
            value={draft.source_system || ""}
            onChange={e => set("source_system", e.target.value || null)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Source table">
          <input
            value={draft.source_table || ""}
            onChange={e => set("source_table", e.target.value || null)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Business impact" className="md:col-span-2">
          <input
            value={draft.business_impact || ""}
            onChange={e => set("business_impact", e.target.value || null)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Formula">
          <input
            value={draft.formula || ""}
            onChange={e => set("formula", e.target.value || null)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Description" className="md:col-span-3">
          <input
            value={draft.description || ""}
            onChange={e => set("description", e.target.value || null)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 pt-4">
        <Button
          type="button"
          variant="transparent"
          className="!text-red-600"
          disabled={saving}
          onClick={() => onDelete(draft.metric_id)}
          icon={<Trash2 className="h-4 w-4" />}
        >
          Delete
        </Button>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline-primary"
            onClick={onCancel}
            icon={<X className="h-4 w-4" />}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={saving}
            disabled={saving || !canSave}
            onClick={() =>
              onSave({
                ...draft,
                category_id: categoryId || null,
                category_key: categoryId || null,
              })
            }
            icon={<Save className="h-4 w-4" />}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  className = "",
  required = false,
  children,
}: {
  label: string;
  className?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="block text-xs font-medium text-gray-600">
        {label}
        {required ? <span className="ml-0.5 text-red-600">*</span> : null}
      </label>
      {children}
    </div>
  );
}

export default DefinitionsPanel;
